import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Loader2, RefreshCw, FileText, CheckCircle2, Undo2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
    fetchCallResults, setCallReviewed, fetchCampaigns
} from "../../services/crmServices/campaignService";
import { bulkAssignLeads, bulkUpdateLeads } from "../../services/crmServices/leadService";
import { fetchAvailableUsers } from "../../services/crmServices/userService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";

const TAG_STYLES = {
    sms_sent: { label: "SMS sent", className: "bg-teal-100 text-teal-700" },
    email_sent: { label: "Email sent", className: "bg-orange-100 text-orange-700" },
    callback_scheduled: { label: "Callback", className: "bg-purple-100 text-purple-700" },
    successful: { label: "Successful", className: "bg-green-100 text-green-700" },
    positive: { label: "Positive", className: "bg-blue-100 text-blue-700" }
};

const ALL_TAGS = Object.keys(TAG_STYLES);

function formatDuration(seconds) {
    if (!seconds && seconds !== 0) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

export function ResultsInbox() {
    const [calls, setCalls] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    // Filters
    const [selectedTags, setSelectedTags] = useState([]);
    const [campaignFilter, setCampaignFilter] = useState("");
    const [reviewedFilter, setReviewedFilter] = useState("false"); // default: unreviewed queue
    const [minDuration, setMinDuration] = useState("");
    const [maxDuration, setMaxDuration] = useState("");

    // Option lists
    const [campaigns, setCampaigns] = useState([]);
    const [agents, setAgents] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);

    // Modals / row state
    const [detailCall, setDetailCall] = useState(null);
    const [assignCall, setAssignCall] = useState(null);
    const [assignAgentId, setAssignAgentId] = useState("");
    const [busyId, setBusyId] = useState(null);

    const limit = 50;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchCallResults({
                page,
                limit,
                tags: selectedTags,
                campaignId: campaignFilter || undefined,
                reviewed: reviewedFilter,
                minDuration: minDuration || undefined,
                maxDuration: maxDuration || undefined
            });
            if (res?.data) {
                setCalls(res.data.calls || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            console.error("Failed to load results", err);
        } finally {
            setLoading(false);
        }
    }, [page, selectedTags, campaignFilter, reviewedFilter, minDuration, maxDuration]);

    useEffect(() => {
        const timer = setTimeout(load, 300);
        return () => clearTimeout(timer);
    }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const [campaignsRes, usersRes, statusRes] = await Promise.all([
                    fetchCampaigns(), fetchAvailableUsers(), fetchLeadStatuses()
                ]);
                setCampaigns(campaignsRes?.data || []);
                const users = (usersRes?.data || []).filter((u) => u.active !== false);
                setAgents(users.map((u) => ({
                    label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
                    value: u._id
                })));
                setStatusOptions((statusRes?.data || []).map((s) => ({ label: s.label || s.value, value: s.label || s.value })));
            } catch (err) {
                console.error("Failed to load filter options", err);
            }
        })();
    }, []);

    const toggleTag = (tag) => {
        setPage(1);
        setSelectedTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
    };

    const handleReview = async (call, reviewed) => {
        setBusyId(call._id);
        try {
            await setCallReviewed(call._id, reviewed);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update");
        } finally {
            setBusyId(null);
        }
    };

    const handleAssign = async () => {
        if (!assignCall?.lead?._id || !assignAgentId) return;
        setBusyId(assignCall._id);
        try {
            await bulkAssignLeads({ leadIds: [assignCall.lead._id], agentIds: [assignAgentId] });
            toast.success("Lead assigned");
            setAssignCall(null);
            setAssignAgentId("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to assign lead");
        } finally {
            setBusyId(null);
        }
    };

    const handleStatusChange = async (call, newStatus) => {
        if (!call.lead?._id || !newStatus) return;
        setBusyId(call._id);
        try {
            await bulkUpdateLeads({ leadIds: [call.lead._id], updates: { status: newStatus } });
            toast.success(`Status set to "${newStatus}"`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update status");
        } finally {
            setBusyId(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / limit));
    const leadName = (lead) => lead ? `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "—" : "—";

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-600 mr-1">Result:</span>
                    {ALL_TAGS.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                            <button
                                key={tag}
                                type="button"
                                onClick={() => toggleTag(tag)}
                                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                                    isSelected
                                        ? "bg-indigo-600 text-white border-indigo-600"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                                }`}
                            >
                                {TAG_STYLES[tag].label}
                            </button>
                        );
                    })}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <Select
                        options={[{ label: "All campaigns", value: "" }, ...campaigns.map((c) => ({ label: c.name, value: c._id }))]}
                        value={campaignFilter}
                        onChange={(v) => { setCampaignFilter(v); setPage(1); }}
                        placeholder="All campaigns"
                        searchable
                    />
                    <Select
                        options={[
                            { label: "Unreviewed", value: "false" },
                            { label: "Reviewed", value: "true" },
                            { label: "All", value: "" }
                        ]}
                        value={reviewedFilter}
                        onChange={(v) => { setReviewedFilter(v); setPage(1); }}
                        placeholder="Unreviewed"
                    />
                    <Input
                        type="number"
                        min="0"
                        value={minDuration}
                        onChange={(e) => { setMinDuration(e.target.value); setPage(1); }}
                        placeholder="Min duration (s)"
                    />
                    <Input
                        type="number"
                        min="0"
                        value={maxDuration}
                        onChange={(e) => { setMaxDuration(e.target.value); setPage(1); }}
                        placeholder="Max duration (s)"
                    />
                    <Button variant="outline" onClick={load}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </Button>
                </div>
            </div>

            {/* Results table */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Call Results ({total})</h3>
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                ) : calls.length === 0 ? (
                    <div className="py-12 text-center">
                        <p className="text-sm text-gray-400">No results{reviewedFilter === "false" ? " to review" : ""}</p>
                        <p className="text-xs text-gray-400 mt-1">Calls appear here when they achieve something: SMS/email sent, callback scheduled, successful or positive call</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50/80">
                                <tr>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lead</th>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Results</th>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Campaign</th>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ended</th>
                                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {calls.map((call) => (
                                    <tr key={call._id} className={`hover:bg-gray-50/50 ${call.reviewedBy ? "opacity-60" : ""}`}>
                                        <td className="px-4 py-2.5">
                                            <p className="text-sm font-medium text-gray-900">{leadName(call.lead)}</p>
                                            <p className="text-xs text-gray-500 font-mono">{call.toNumber}</p>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex flex-wrap gap-1">
                                                {(call.resultTags || []).map((tag) => (
                                                    <Badge key={tag} className={`text-[10px] ${TAG_STYLES[tag]?.className || "bg-gray-100 text-gray-600"}`}>
                                                        {TAG_STYLES[tag]?.label || tag}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-sm text-gray-600 font-mono">{formatDuration(call.durationSeconds)}</td>
                                        <td className="px-4 py-2.5 w-40" onClick={(e) => e.stopPropagation()}>
                                            <Select
                                                options={statusOptions}
                                                value={call.lead?.status || ""}
                                                onChange={(v) => handleStatusChange(call, v)}
                                                placeholder="Status..."
                                                searchable
                                            />
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">{call.campaign?.name || "—"}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-500">
                                            {call.endedAt ? new Date(call.endedAt).toLocaleString() : "—"}
                                            {call.reviewedBy && (
                                                <p className="text-[10px] text-gray-400 mt-0.5">
                                                    Reviewed by {call.reviewedBy.firstName || ""} {call.reviewedBy.lastName || ""}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {(call.transcript || call.callSummary || call.recordingUrl) && (
                                                    <Button variant="ghost" size="sm" onClick={() => setDetailCall(call)} title="Transcript / recording">
                                                        <FileText className="h-4 w-4 text-indigo-600" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => { setAssignCall(call); setAssignAgentId(""); }}
                                                    title="Assign lead to agent"
                                                >
                                                    <UserPlus className="h-4 w-4 text-gray-600" />
                                                </Button>
                                                {call.reviewedBy ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        disabled={busyId === call._id}
                                                        onClick={() => handleReview(call, false)}
                                                        title="Mark as unreviewed"
                                                    >
                                                        <Undo2 className="h-4 w-4 text-gray-500" />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        disabled={busyId === call._id}
                                                        onClick={() => handleReview(call, true)}
                                                        title="Mark as reviewed"
                                                    >
                                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Transcript / detail modal */}
            <Dialog open={!!detailCall} onOpenChange={(v) => !v && setDetailCall(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Call Details</DialogTitle>
                        <DialogDescription>
                            {leadName(detailCall?.lead)} — {detailCall?.toNumber} • {formatDuration(detailCall?.durationSeconds)}
                        </DialogDescription>
                    </DialogHeader>
                    {detailCall?.recordingUrl && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Recording</p>
                            <audio controls className="w-full" src={detailCall.recordingUrl} />
                        </div>
                    )}
                    {detailCall?.callSummary && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Summary</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{detailCall.callSummary}</p>
                        </div>
                    )}
                    {detailCall?.transcript && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Transcript</p>
                            <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans max-h-80 overflow-y-auto">{detailCall.transcript}</pre>
                        </div>
                    )}
                    {detailCall && Object.keys(detailCall.customAnalysisData || {}).length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Analysis Data</p>
                            <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">{JSON.stringify(detailCall.customAnalysisData, null, 2)}</pre>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Assign modal */}
            <Dialog open={!!assignCall} onOpenChange={(v) => !v && setAssignCall(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Assign Lead</DialogTitle>
                        <DialogDescription>
                            Assign {leadName(assignCall?.lead)} to an agent. The agent gets notified automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <Select
                        options={agents}
                        value={assignAgentId}
                        onChange={setAssignAgentId}
                        placeholder="Select agent..."
                        searchable
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setAssignCall(null)}>Cancel</Button>
                        <Button
                            onClick={handleAssign}
                            disabled={!assignAgentId || busyId === assignCall?._id}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {busyId === assignCall?._id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                            Assign
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
